import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'

interface RevealProps {
    children: ReactNode
    className?: string
}

/**
 * Plays the line and fade reveals when the block scrolls into view.
 * Blocks already on screen at load stay visible, so nothing flashes.
 */
export const Reveal: FC<RevealProps> = ({ children, className }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const [phase, setPhase] = useState<'in' | 'pre'>('in')

    useEffect(() => {
        const element = ref.current

        if (!element) return

        if (element.getBoundingClientRect().top < window.innerHeight * 0.9) return

        setPhase('pre')

        const observer = new IntersectionObserver(entries => {
            if (!entries.some(entry => entry.isIntersecting)) return

            setPhase('in')
            observer.disconnect()
        }, { rootMargin: '0px 0px -12% 0px' })

        observer.observe(element)

        return () => observer.disconnect()
    }, [])

    return (
        <div ref={ref} data-reveal={phase} className={className}>
            {children}
        </div>
    )
}
